import axios from "axios";
import { getBaseUrl } from "./getBaseUrl";

/**
 * examClient — the HTTP client for public exam candidates.
 *
 * ⚠️ Deliberately NOT apiClient. Three reasons, each of which would be a bug:
 *
 *   1. apiClient attaches the LMS `token` from localStorage. A candidate has no
 *      LMS account, and a stray token from a previous user on a shared lab
 *      machine must never be sent with an exam request.
 *   2. apiClient's 401 handler logs the user out and redirects to /login.
 *      During an exam that would throw a candidate out of a timed paper because
 *      of one expired unrelated token.
 *   3. apiClient sends X-Branch-Id from the admin's branch selection. Tenancy
 *      here comes from the URL slug and nothing else.
 *
 * The candidate token lives under its own key, so it cannot collide with an LMS
 * session in the same browser.
 */

const TOKEN_KEY = "vc-exam-candidate-token";
const SLUG_KEY = "vc-exam-candidate-slug";

// The same API apiClient talks to — including an explicit REACT_APP_API_URL,
// which this file used to ignore (a QA or on-prem build's candidates were sent
// to production's API).
const baseURL = getBaseUrl();

const examClient = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

// ── token storage ──────────────────────────────────────────────────────
// Scoped to the drive: a candidate who sat drive A yesterday must not present
// that token to drive B today. The server checks this too, but keeping the
// client honest avoids a confusing 401 on the landing page.
//
// sessionStorage is per-tab and dies with the tab, which is the right lifetime
// for an exam on a shared lab machine. The in-memory copy is the fallback when
// storage is blocked (private mode, locked-down kiosk browsers).
const memory = { token: null, slug: null };

export function setCandidateToken(slug, token) {
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token);
    window.sessionStorage.setItem(SLUG_KEY, slug);
  } catch { /* private mode — the in-memory value below still works */ }
  memory.token = token;
  memory.slug = slug;
}

export function getCandidateToken(slug) {
  if (memory.token && memory.slug === slug) return memory.token;
  try {
    const stored = window.sessionStorage.getItem(TOKEN_KEY);
    const storedSlug = window.sessionStorage.getItem(SLUG_KEY);
    if (stored && storedSlug === slug) {
      memory.token = stored;
      memory.slug = storedSlug;
      return stored;
    }
  } catch { /* noop */ }
  return null;
}

export function clearCandidateToken() {
  memory.token = null;
  memory.slug = null;
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(SLUG_KEY);
  } catch { /* noop */ }
}

examClient.interceptors.request.use((config) => {
  // The slug is always the first path segment after /public/exams/.
  const match = /\/public\/exams\/([^/?]+)/.exec(config.url || "");
  const slug = match?.[1];
  const token = slug ? getCandidateToken(slug) : memory.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default examClient;
export const EXAM_TOKEN_KEY = TOKEN_KEY;
